import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation18() {
  const { lesson, course, accent } = findLesson("piano-foundation", "18");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF18-Inversions-Animated.mp4"
        caption={`Three inversions of C major — same notes, the top voice changes character each rotation`}
        hint={`≈ 22 sec · the hand slides, never jumps`}
      />
      <KeyHighlights>
        <Highlight>Every major scale gives you <strong>seven chords</strong>: 1-4-5 are major, 2-3-6 are minor, 7 is diminished.</Highlight>
        <Highlight>A triad has <strong>three positions</strong> — root position, plus two inversions: first (start on the 3rd) and second (start on the 5th). Same three notes, three orderings. Root position isn't an inversion; it's what the other two are inversions of.</Highlight>
        <Highlight>Choose inversions by finding <strong>common notes</strong> between consecutive chords. Hold the common note still; only the other fingers move.</Highlight>
        <Highlight>The left hand <strong>always plays the chord's root</strong>, regardless of which inversion the right hand is on. That keeps the chord sounding settled and keeps its name unambiguous — an inversion on its own can be mistaken for a different chord.</Highlight>
        <Highlight>Plan everything by <strong>writing it down</strong> — the scale in a circle, the triads, the chord qualities, the progression. Then play. The writing IS the practice.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The plan-before-you-play approach" title="Write the scale in a circle" accent={accent.hex}>
        <Pull>
          As you keep writing and revisiting these concepts, especially theory, you
          know, you can definitely grow as a stronger piano player. You need to write
          this information down.
        </Pull>

        <p>
          The first move on any new key is to{" "}
          <strong>write the scale in a circle</strong>. For G major:{" "}
          <span className="font-mono text-amber-300">G-A-B-C-D-E-F♯</span>, looping back to
          G. The circle layout matters because it makes triad-building visual.
        </p>

        <Vocab>
          <Term name="Triad" def="a three-note chord built by stacking thirds. From the scale circle: skip a note, play the next; skip again, play the next." />
          <Term name="Inversion" def="reordering a chord's notes. Same three pitches, different starting note." />
          <Term name="Voicing" def="another name for chord shape. Any of the three positions counts as a different voicing." />
          <Term name="Voice leading" def="the smoothest path from one chord to the next, by finding common notes and moving the others minimally." />
        </Vocab>
      </Insight>

      <Insight eyebrow="The seven chords of G major" title="Build them by skip-skip on the circle" accent={accent.hex}>
        <p>
          From the scale circle, build a triad on every note by skipping one and playing the
          next, twice:
        </p>

        <div className="overflow-x-auto rounded-xl border border-nsm-border bg-nsm-card/50 p-4 text-[13px]">
          <table className="w-full">
            <thead>
              <tr className="text-nsm-dim">
                <th className="text-left">Degree</th>
                <th>Triad</th>
                <th>Quality</th>
                <th>Vibe</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-t border-nsm-border"><td>1</td><td>G-B-D</td><td className="text-emerald-300">G major</td><td>happy / victorious</td></tr>
              <tr className="border-t border-nsm-border"><td>2</td><td>A-C-E</td><td className="text-rose-300">A minor</td><td>serious / sad</td></tr>
              <tr className="border-t border-nsm-border"><td>3</td><td>B-D-F♯</td><td className="text-rose-300">B minor</td><td>serious / sad</td></tr>
              <tr className="border-t border-nsm-border"><td>4</td><td>C-E-G</td><td className="text-emerald-300">C major</td><td>happy</td></tr>
              <tr className="border-t border-nsm-border"><td>5</td><td>D-F♯-A</td><td className="text-emerald-300">D major</td><td>happy</td></tr>
              <tr className="border-t border-nsm-border"><td>6</td><td>E-G-B</td><td className="text-rose-300">E minor</td><td>serious / melancholic</td></tr>
              <tr className="border-t border-nsm-border"><td>7</td><td>F♯-A-C</td><td className="text-amber-300">F♯ diminished</td><td>tense / scary</td></tr>
            </tbody>
          </table>
        </div>

        <KeyIdea>
          <strong>1-4-5 are major. 2-3-6 are minor. 7 is diminished.</strong> That's the
          formula in any major scale. In F major it'd be F-Bb-C major (1-4-5), Gm-Am-Dm
          (2-3-6), E diminished (7). Same pattern, different letters.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Root position and its two inversions" title="Same chord, three orderings" accent={accent.hex}>
        <p>
          Each triad has three notes. Starting from each one in turn gives you three shapes:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li><strong>Root position</strong> — start with the chord's name. G major = G-B-D.</li>
          <li><strong>First inversion</strong> — start on the 3rd. G major = B-D-G.</li>
          <li><strong>Second inversion</strong> — start on the 5th. G major = D-G-B.</li>
        </ul>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "G3", color: "#22c55e", label: "Root · G" },
            { pitch: "B3", color: "#3b82f6", label: "1st · B" },
            { pitch: "D4", color: "#a855f7", label: "2nd · D" },
            { pitch: "G4", color: "#22c55e", label: "G" },
            { pitch: "B4", color: "#3b82f6", label: "B" },
          ]}
          height={170}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="18"
          id="g-triad"
          mode="chord"
          title="G major triad · root position"
          prompt="Play G major in root position — all three notes together: G, B, D."
          notes={["G3", "B3", "D4"]}
          startOctave={3}
          octaves={2}
        />

        <p className="prose-nsm text-sm">
          Three different ways to play the same G major chord on the keyboard. Pick the
          one whose lowest note is closest to where your hand already is.
        </p>
      </Insight>

      <Insight eyebrow="The progression" title="G → C → Em → D · 1 - 4 - 6 - 5" accent={accent.hex}>
        <p>
          The progression chosen for the demonstration: </p>

        <p className="font-mono text-[14px] text-amber-300">
          G major (1) → C major (4) → E minor (6) → D major (5)
        </p>

        <p>
          Played as straight root-position chords, the right hand has to <em>jump</em> all
          over the keyboard between each chord. Played with smart inversions, the right hand
          barely moves — and the song sounds far more professional.
        </p>
      </Insight>

      <Insight eyebrow="The connecting trick" title="Find the common note · don't move it" accent={accent.hex}>
        <Pull>
          Whatever note is in common, when you look at your keyboard from here, don't
          change the common note. Keep the same finger held on or pressed for the next
          chord as well. Why? Because it's just common sense.
        </Pull>

        <p>
          Walk through the progression chord-by-chord, asking the same question each time:
          <em> what's in common with the previous chord?</em>
        </p>

        <Steps>
          <Step>
            <strong>G → C.</strong> G major (G-B-D) shares <strong>G</strong> with C major.
            Play C major as G-C-E (its second inversion). The G stays put.
          </Step>
          <Step>
            <strong>C → Em.</strong> C major (G-C-E) shares <strong>both G and E</strong> with
            E minor. Play Em as G-B-E (its first inversion). Only the C in the middle drops
            to B.
          </Step>
          <Step>
            <strong>Em → D.</strong> E minor (G-B-E) has <strong>nothing in common</strong>{" "}
            with D major. So move to the closest position: F♯-A-D (D's first inversion).
            Every finger moves — but only by a step or two.
          </Step>
        </Steps>

        <Tip>
          Try starting on a different inversion of G to see if the whole progression flows
          even more smoothly. Begin with B-D-G instead of G-B-D, and you'll find that the
          common-note connections often improve. Plan it on paper first — It's a real rule.
        </Tip>
      </Insight>

      <Insight eyebrow="The left hand" title="Always plays the chord's root" accent={accent.hex}>
        <p>
          One non-negotiable rule: regardless of which inversion the right hand is using,
          the <strong>left hand always plays the chord's root</strong>. For G major, even if
          your RH is on B-D-G or D-G-B, your LH plays G in the bass. Otherwise, the chord
          stops sounding major — the bottom note dictates the chord's identity.
        </p>

        <KeyIdea>
          So while the RH dances between inversions for smoothness, the LH steps cleanly
          through G → C → E → D — the simple roots of the four chords. Two completely
          different jobs in the same body.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The writing routine" title="The order the lesson recommends every time" accent={accent.hex}>
        <Steps>
          <Step>Write the scale in a neat round circle.</Step>
          <Step>Build the seven triads using the skip-skip pattern.</Step>
          <Step>Classify each chord — 1-4-5 major, 2-3-6 minor, 7 diminished.</Step>
          <Step>Pick a chord progression for the song you're working on.</Step>
          <Step>Write each chord in all three positions — root position and its two inversions — in their own neat round circles.</Step>
          <Step>Find the common notes between consecutive chords. Plan the smoothest path.</Step>
          <Step>Then — and only then — sit at the piano and play.</Step>
        </Steps>

        <Pull>
          It's the planning which you need to really work on and you need to write these
          things down.
        </Pull>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-19"
        label="Lesson 19 · Oom-Pah & Waltz"
        why="With chord theory and inversions in your toolkit, the next lesson uses both — the Oom-Pah pattern in 4/4 and the Waltz in 3/4 — to give every chord change a built-in groove."
      />
    </LessonShell>
  );
}
